import React from 'react';

const FaceRecognitionCard = ({ faceData, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <div className="text-red-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600">Unable to load face recognition data</p>
          <p className="text-sm text-gray-400">{error.message}</p>
        </div>
      </div>
    );
  }

  const stats = faceData?.stats?.overview || {};
  const recentFaces = faceData?.recentFaces || [];
  const relationshipBreakdown = faceData?.stats?.relationshipBreakdown || [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <div className="p-2 bg-purple-100 rounded-lg mr-3">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          Face Recognition
        </h3>
        <span className="text-sm text-gray-500">
          {stats.totalFaces || 0} registered faces
        </span>
      </div>

      {/* Face Recognition Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <div className="text-2xl font-bold text-purple-600">
            {stats.totalFaces || 0}
          </div>
          <div className="text-sm text-gray-600">Registered</div>
        </div>
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">
            {stats.totalRecognitions || 0}
          </div>
          <div className="text-sm text-gray-600">Recognitions</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {stats.avgConfidence ? `${(stats.avgConfidence * 100).toFixed(0)}%` : 'N/A'}
          </div>
          <div className="text-sm text-gray-600">Accuracy</div>
        </div>
      </div>

      {/* Relationship Breakdown */}
      {relationshipBreakdown.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Relationships</h4>
          <div className="flex flex-wrap gap-2">
            {relationshipBreakdown.map((rel, index) => (
              <div key={index} className="flex items-center space-x-2 bg-gray-100 rounded-full px-3 py-1">
                <div className={`w-3 h-3 rounded-full ${
                  rel._id === 'family' ? 'bg-red-400' :
                  rel._id === 'friend' ? 'bg-blue-400' :
                  rel._id === 'colleague' ? 'bg-green-400' :
                  'bg-gray-400'
                }`}></div>
                <span className="text-sm text-gray-700 capitalize">{rel._id}</span>
                <span className="text-sm font-medium text-gray-900">{rel.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recently Seen Faces */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Recently Seen</h4>
        {recentFaces.length > 0 ? (
          <div className="space-y-3">
            {recentFaces.slice(0, 4).map((face, index) => (
              <div key={face._id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                    {face.personName ? face.personName.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {face.personName || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {face.relationship || 'Unknown'} • {face.recognitionCount || 0} times
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-400">
                    {face.lastSeen 
                      ? new Date(face.lastSeen).toLocaleDateString()
                      : 'Never'
                    }
                  </div>
                  <div className="text-xs text-gray-500">
                    {face.daysSinceLastSeen !== undefined 
                      ? `${face.daysSinceLastSeen} days ago`
                      : ''
                    }
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <div className="text-gray-400 mb-2">
              <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-sm text-gray-500">No faces registered yet</p>
            <button className="mt-2 text-sm text-purple-600 hover:text-purple-800">
              Register your first face
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceRecognitionCard;
